import { useState } from "react";
import { eng, activeRole } from "@/lib/engineApi";
import { useGrid } from "@/state/grid";
import { onDocApproved } from "@/lib/events";

// Documents (TDD) view — faithful React port of the engine's vTDD().
// Reads docs/categories from the engine; mutates tddDocs through the bridge.
const STATUS_COLOR: Record<string, string> = {
  approved: "var(--grass)",
  pending: "var(--amber)",
  rejected: "var(--rust)",
  review: "var(--steel)",
};

export default function TddView() {
  const version = useGrid((s) => s.version);
  const bump = useGrid((s) => s.bump);
  void version; // re-render on engine changes
  const [showAdd, setShowAdd] = useState(false);

  const e = eng();
  const D = e.D;
  const role = activeRole();
  const cats = D.tddCategories;
  const filtCat = e.state.tddCat ?? "all";
  const search = (e.state.tddSearch || "").toLowerCase();

  const docs = e.tddDocs.filter(
    (d: any) =>
      (filtCat === "all" || d.cat === filtCat) &&
      (!search ||
        d.name.toLowerCase().includes(search) ||
        d.ref.toLowerCase().includes(search) ||
        d.notes.toLowerCase().includes(search)),
  );

  const setCat = (id: string) => {
    e.state.tddCat = id;
    bump();
  };
  const setSearch = (v: string) => {
    e.state.tddSearch = v;
    bump();
  };
  const approve = (id: string) => {
    const d = e.tddDocs.find((x: any) => x.id === id);
    if (d) {
      d.status = "approved";
      e.saveToStorage();
      bump();
      e.toast(`"${d.name}" approved`, "success");
      onDocApproved(d.id, d.name);
    }
  };
  const remove = (id: string) => {
    const d = e.tddDocs.find((x: any) => x.id === id);
    if (!d) return;
    if (confirm(`Remove "${d.name}" from documents?`)) {
      e.tddDocs = e.tddDocs.filter((x: any) => x.id !== id);
      e.saveToStorage();
      bump();
      e.toast("Document removed", "info");
    }
  };
  const exportList = () => {
    const header = "Name\tRef\tRev\tDate\tCategory\tStatus\tBy\tNotes";
    const dataRows = e.tddDocs.map(
      (d: any) => `${d.name}\t${d.ref}\t${d.rev}\t${d.date}\t${d.cat}\t${d.status}\t${d.by}\t${d.notes}`,
    );
    const blob = new Blob([[header, ...dataRows].join("\n")], { type: "text/plain" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = "Ballyroe_Documents.txt";
    a.click();
    URL.revokeObjectURL(u);
    e.toast("Document list exported", "success");
  };

  const catTabs = [{ id: "all", label: "All", icon: "[FL]", color: "var(--dim)" }, ...cats];

  return (
    <div className="tddwrap">
      <div className="vhead">
        <h1 className="serif">Documents</h1>
        <div className="vs">Project document repository — {D.project.name}</div>
        <div className="vtag">{e.tddDocs.length} documents</div>
      </div>

      <div className="tdd-toolbar">
        <div className="tdd-cats">
          {catTabs.map((c: any) => {
            const count = e.tddDocs.filter((d: any) => d.cat === c.id).length;
            return (
              <button
                key={c.id}
                className={`tdd-cat-tab ${filtCat === c.id ? "sel" : ""}`}
                style={{ ["--tc" as any]: c.color || "var(--hiviz)" }}
                onClick={() => setCat(c.id)}
              >
                {c.icon || "[FL]"} {c.label}
                {filtCat === c.id && c.id !== "all" ? <span className="tcc">{count}</span> : null}
              </button>
            );
          })}
        </div>
        <div className="tdd-actions">
          <input
            className="tdd-search"
            placeholder="Search docs…"
            value={e.state.tddSearch || ""}
            onChange={(ev) => setSearch(ev.target.value)}
          />
          {role.canEdit || role.canApprove ? (
            <button className="export-btn go" onClick={() => setShowAdd(true)}>
              + Add document
            </button>
          ) : null}
          <button className="export-btn" onClick={exportList}>
            ⬇ Export list
          </button>
        </div>
      </div>

      <div className="tdd-stats">
        {cats.map((c: any) => {
          const n = e.tddDocs.filter((d: any) => d.cat === c.id).length;
          const ap = e.tddDocs.filter((d: any) => d.cat === c.id && d.status === "approved").length;
          return n ? (
            <div className="tdd-stat" key={c.id}>
              <span>{c.icon}</span>
              <b>
                {ap}/{n}
              </b>
              <span>{c.label}</span>
            </div>
          ) : null;
        })}
      </div>

      <div className="tdd-list">
        {docs.length ? (
          docs.map((d: any) => {
            const cat = cats.find((c: any) => c.id === d.cat) || { icon: "[F]", color: "var(--dim)", label: d.cat };
            const sc = STATUS_COLOR[d.status] || "var(--dim)";
            return (
              <div className="tdd-row" key={d.id}>
                <div className="tdd-icon" style={{ background: `${cat.color}22`, color: cat.color }}>
                  {cat.icon}
                </div>
                <div className="tdd-main">
                  <div className="tdd-name">{d.name}</div>
                  <div className="tdd-meta mono">
                    {d.ref} · Rev {d.rev} · {d.date}
                  </div>
                  <div className="tdd-notes">{d.notes}</div>
                </div>
                <div className="tdd-right">
                  <span
                    className="pill"
                    style={{ background: `${sc}22`, color: sc, border: `1px solid ${sc}44` }}
                  >
                    {String(d.status).toUpperCase()}
                  </span>
                  <div className="tdd-by">{d.by}</div>
                  {role.canApprove && d.status !== "approved" ? (
                    <button
                      className="obtn ok"
                      style={{ fontSize: 10, padding: "4px 10px" }}
                      onClick={() => approve(d.id)}
                    >
                      Approve
                    </button>
                  ) : null}
                  {role.canEdit || role.canApprove ? (
                    <button
                      className="obtn nu"
                      style={{ fontSize: 10, padding: "4px 10px" }}
                      onClick={() => remove(d.id)}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })
        ) : (
          <div className="empty-state">
            <div className="es-ico">🔍</div>
            <div className="es-t">
              {search ? "No matches" : filtCat !== "all" ? "Nothing in this category" : "No documents yet"}
            </div>
            <div className="es-s">
              {search
                ? `Nothing in the document repository matches "${search}". Try a different keyword or clear filters.`
                : filtCat !== "all"
                  ? "Try a different category, or clear the filter to see everything."
                  : "Add the first document using the button above."}
            </div>
          </div>
        )}
      </div>

      {showAdd && <AddDocModal onClose={() => setShowAdd(false)} />}

      <div className="card" style={{ marginTop: 18 }}>
        <h3>Project parties</h3>
        <div className="csub">Roles across the project</div>
        <table className="bom">
          <tbody>
            <tr>
              <td style={{ color: "var(--dim)", width: 200 }}>Client / developer</td>
              <td>{D.project.client}</td>
            </tr>
            <tr>
              <td style={{ color: "var(--dim)" }}>EPC / piling contractor</td>
              <td>{D.project.epc}</td>
            </tr>
            <tr>
              <td style={{ color: "var(--dim)" }}>Mounting structure</td>
              <td>
                {D.project.structure} ({D.project.system})
              </td>
            </tr>
            <tr>
              <td style={{ color: "var(--dim)" }}>Inverter engineering</td>
              <td>{D.project.inverterEng}</td>
            </tr>
            <tr>
              <td style={{ color: "var(--dim)" }}>Location</td>
              <td>
                {D.project.location} · <span className="mono">{D.project.gps}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AddDocModal({ onClose }: { onClose: () => void }) {
  const e = eng();
  const bump = useGrid((s) => s.bump);
  const cats = e.D.tddCategories;
  const [f, setF] = useState({
    name: "",
    cat: cats[0]?.id ?? "design",
    ref: "",
    rev: "1",
    date: "",
    by: "",
    notes: "",
  });
  const upd = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));

  const save = () => {
    if (!f.name.trim()) {
      e.toast("Document name is required", "warn");
      return;
    }
    e.tddDocs.push({
      id: "tdd-" + Date.now().toString(36),
      cat: f.cat,
      name: f.name.trim(),
      ref: f.ref,
      rev: f.rev,
      date: f.date,
      by: f.by,
      status: "pending",
      notes: f.notes,
    });
    e.saveToStorage();
    bump();
    e.toast(`Document "${f.name.trim()}" added`, "success");
    onClose();
  };

  return (
    <div className="tdd-modal" style={{ display: "flex" }}>
      <div className="tdd-modal-box">
        <div className="tdd-modal-hd">Add document</div>
        <div className="tdd-form">
          <label>
            Name
            <input value={f.name} onChange={(ev) => upd("name", ev.target.value)} placeholder="Document name" />
          </label>
          <label>
            Category
            <select value={f.cat} onChange={(ev) => upd("cat", ev.target.value)}>
              {cats.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.icon} {c.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Reference No.
            <input value={f.ref} onChange={(ev) => upd("ref", ev.target.value)} placeholder="e.g. CNC-BV748-E04" />
          </label>
          <label>
            Rev.
            <input value={f.rev} onChange={(ev) => upd("rev", ev.target.value)} style={{ width: 70 }} />
          </label>
          <label>
            Date
            <input value={f.date} onChange={(ev) => upd("date", ev.target.value)} placeholder="YYYY-MM" />
          </label>
          <label>
            Author / Company
            <input value={f.by} onChange={(ev) => upd("by", ev.target.value)} placeholder="e.g. Conecon GmbH" />
          </label>
          <label>
            Notes
            <input value={f.notes} onChange={(ev) => upd("notes", ev.target.value)} placeholder="Short description" />
          </label>
        </div>
        <div className="tdd-modal-btns">
          <button className="obtn ok" onClick={save}>
            Save document
          </button>
          <button className="obtn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
